<template>
  <Slideover :visible="visible">
    <div>
      <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
        <div class="flex items-start justify-between space-x-3">
          <h2 class="text-lg leading-7 font-medium">
            {{ title }}
          </h2>
          <div class="h-7 flex items-center">
            <button
              type="button"
              aria-label="Close panel"
              @click="$emit('toggle-slideover')"
              class="text-green-300 hover:text-white transition ease-in-out duration-150"
            >
              <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <!-- Main -->
      <div class="relative flex flex-1 flex-col py-3 px-2 mx-4 space-y-3">
        <div class="h-32 flex justify-center items-center">
          <icon-fgt
            v-if="selectedCart"
            :name="selectedLogo"
            class="w-32 h-32"
          />
          <span v-else class="text-gray-500">
            Please select a Cart to proceed
          </span>
        </div>
        <div>
          <label :for="`cart-select-${_uid}`" class="block text-sm font-medium leading-5 text-gray-700">Select Cart</label>
          <select
            :id="`cart-select-${_uid}`"
            v-model="cartId"
            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option selected disabled>Please select a cart</option>
            <option
              v-for="cart in carts"
              :key="cart.ID"
              :value="cart.ID"
            >
              {{ cart.CartName }}
            </option>
          </select>
        </div>

        <div>
          <label
            :for="`code-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Discount Code:
          </label>
          <div class="mt-1 flex rounded-md shadow-sm">
            <div class="relative flex items-stretch flex-grow focus-within:z-10">
              <span class="block text-sm font-medium leading-5 text-gray-700 mt-2.5">CS-</span><input
                :value="code"
                @change="code = null; code = $event.target.value.toUpperCase()"
                :id="`code-${_uid}`"
                type="text"
                :disabled="! selectedCart"
                class="form-input block w-full py-2 px-3 uppercase rounded-none rounded-l-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <button
              @click="generateRandomCode"
              class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-r-md text-gray-700 bg-gray-50 hover:text-gray-500 hover:bg-white focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
                <icon
                  set="outline"
                  name="refresh"
                  class="h-5 w-5 text-gray-400"
                />
              <span class="ml-2">Generate</span>
            </button>
          </div>
        </div>

        <div>
          <label :for="`discount-type-${_uid}`" class="block text-sm font-medium leading-5 text-gray-700">Discount Type</label>
          <select
            :id="`discount-type-${_uid}`"
            v-model="type"
            :disabled="! selectedCart"
            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option selected disabled>Please select a type</option>
            <option value="percentage">Percentage</option>
            <option value="fixed_amount">Fixed Amount</option>
            <option value="free-shipping">Free Shipping</option>
          </select>
        </div>

        <div v-if="hasValue">
          <label
            :for="`value-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Value:
          </label>
          <div v-if="type === 'fixed_amount'">
            <div class="mt-1 relative rounded-md shadow-sm">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm sm:leading-5">
                  $
                </span>
              </div>
              <input
                :value="value"
                @change="value = null; value = decimal($event.target.value, 2)"
                :id="`value-${_uid}`"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="mt-1 form-input block w-full py-2 pl-7 pr-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              />
            </div>
          </div>
          <div v-if="type === 'percentage'">
            <div class="mt-1 relative rounded-md shadow-sm">
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm sm:leading-5">
                  %
                </span>
              </div>
              <input
                :value="value"
                @change="value = null; value = decimal($event.target.value, 0)"
                :id="`value-${_uid}`"
                type="number"
                step="1"
                placeholder="0"
                class="mt-1 form-input block w-full py-2 pl-3 pr-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              />
            </div>
          </div>
        </div>

        <div>
          <label
            :for="`note-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Note:
          </label>
          <textarea
            v-model="note"
            :id="`note-${_uid}`"
            :disabled="! selectedCart"
            rows="3"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
          ></textarea>
        </div>

      </div>

      <HttpError :error="error" dismissable />

      <!-- Footer -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="button"
            @click="$emit('toggle-slideover')"
            class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
        </span>
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="submit"
            class="inline-flex justify-center items-center space-x-2 py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green active:bg-green-700 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="processing || ! canProcess"
            @click="process"
          >
            <span>Submit</span>
            <LoadingSpinner v-if="processing" />
          </button>
        </span>
      </div>
    </div>
  </Slideover>
</template>
<script>
import allCarts from "@/data/carts.json";
import { ApiFactory } from "@/api/ApiFactory.js";
import Slideover from "@/components/layout/Slideover.vue";

const Discounts = ApiFactory.get("discounts");

export default {
  name: "CreateDiscount",
  components: {
    Slideover
  },
  props: {
    title: {
      type: String,
      default: "Title"
    },
    footer: {
      type: Boolean,
      default: true
    },
    visible: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      cartId: null,
      code: null,
      type: null,
      value: null,
      note: null,
      error: null,
      processing: false,
    }
  },
  computed: {
    carts() {
      return allCarts.filter((cart) => [22, 23, 31, 32].includes(cart.ID));
    },
    selectedCart() {
      return this.carts.find((cart) => cart.ID === this.cartId);
    },
    selectedLogo() {
      return this.selectedCart?.LogoName;
    },
    canProcess() {
      return this.selectedCart !== undefined
        && this.code
        && this.type
        && (! this.hasValue || this.value)
        && this.note;
    },
    hasValue() {
      return ['percentage', 'fixed_amount'].includes(this.type);
    },
    valueType() {
      if (this.type === 'free-shipping') {
        return 'percentage';
      }
      return this.type;
    },
    targetType() {
      if (this.type === 'free-shipping') {
        return 'shipping_line';
      }
      return 'line_item';
    }
  },
  watch: {
    type(type) {
      if (type === 'free-shipping') {
        this.value = 100;
        return;
      }
      this.value = null;
    }
  },
  created() {
    this.generateRandomCode();
  },
  methods: {
    generateRandomCode() {
      this.code = Math.random()
        .toString(36)
        .substring(2, 10)
        .toUpperCase();
    },
    decimal(value, digits) {
      if (digits === undefined) {
        digits = 2;
      }

      return Number(value).toFixed(digits);
    },
    process() {
      this.processing = true;
      this.error = null;
      this.$emit('creating-discount');

      Discounts.store({
          cartId: this.selectedCart?.ID,
          code: "CS-"+this.code,
          valueType: this.valueType,
          value: this.value,
          targetType: this.targetType,
          note: this.note
        })
        .then((response) => {
          this.$emit('discount-created', response.data.body?.discount_code?.code);
          this.$emit("toggle-slideover");
        })
        .catch((error) => this.error = error)
        .finally(() => this.processing = false);
    }
  }
};
</script>
